import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../AuthProvider'
import { useI18n } from '../../../i18n'
import { API_ENDPOINTS } from '../../../config/api'
import { featureFlags } from '../../../config/featureFlags'
import { PolicyDocumentUuidCell } from '../../policies/PolicyDocumentUuidCell'
import { SearchField } from '../../ui/SearchField'
import { Button } from '../../ui/Button'
import { GovernancePickerModal } from '../../governance/GovernancePickerModal'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

export type GovernanceLibraryRow = {
  id: string
  uuid?: string
  title: string
  type?: string
  status?: string
  displayId?: string
  currentVersionId?: string | null
}

type Props = {
  open: boolean
  onClose: () => void
  selectedIds: string[]
  onSelect: (policyId: string, policyVersionId?: string | null) => void
}

export function QuestionGovernanceLibraryPicker({ open, onClose, selectedIds, onSelect }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const [rows, setRows] = useState<GovernanceLibraryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!open) return
    setSearch('')
  }, [open])

  useEffect(() => {
    if (!open || !token || !featureFlags.DOCUMENT_LIFECYCLE_V1) return
    setLoading(true)
    fetch(API_ENDPOINTS.GOVERNANCE_POLICIES, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setRows(data.policies || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [open, token])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(
      (row) =>
        String(row.title || '').toLowerCase().includes(q) ||
        String(row.displayId || '').toLowerCase().includes(q) ||
        String(row.uuid || row.id).toLowerCase().includes(q) ||
        String(row.type || '').toLowerCase().includes(q),
    )
  }, [rows, search])

  if (!featureFlags.DOCUMENT_LIFECYCLE_V1) return null

  return (
    <GovernancePickerModal
      open={open}
      onClose={onClose}
      title={t('governance.documentLifecycle.selectPolicies', 'Select from Policies & Governance')}
      titleId="question-policy-library-modal-title"
    >
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={t('governance.policies.searchPlaceholder', 'Search policies…')}
        minWidth="100%"
      />

      {loading ? <p className={listStyles.pickerRowMeta}>{t('common.loading', 'Loading…')}</p> : null}

      {!loading && filteredRows.length === 0 ? (
        <p className={listStyles.pickerEmpty}>
          {t('governance.policies.noPolicies', 'No documents found.')}
        </p>
      ) : (
        <ul className={listStyles.pickerList}>
          {filteredRows.map((row) => {
            const selected = selectedIds.includes(row.id)
            const rowClass = [
              listStyles.pickerRow,
              selected ? listStyles.pickerRowDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <li key={row.id} className={rowClass}>
                <div className="min-w-0 flex-1">
                  <p className={listStyles.pickerRowTitle}>{row.title}</p>
                  {row.displayId ? (
                    <p className={`${listStyles.pickerRowMeta} ${listStyles.pickerRowMono}`}>{row.displayId}</p>
                  ) : null}
                  <div className="mt-1">
                    <PolicyDocumentUuidCell uuid={row.uuid || row.id} />
                  </div>
                  {row.type || row.status ? (
                    <p className={listStyles.pickerRowMeta}>
                      {[row.type, row.status].filter(Boolean).join(' · ')}
                    </p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  size="small"
                  color={selected ? 'outline' : 'primary'}
                  disabled={selected}
                  className="shrink-0"
                  onClick={() => onSelect(row.id, row.currentVersionId)}
                >
                  {selected
                    ? t('governance.documentLifecycle.linked', 'Linked')
                    : t('governance.documentLifecycle.linkDocument', 'Link')}
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </GovernancePickerModal>
  )
}
